import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { BellRing, UserCheck } from 'lucide-react';
import { startRinging, stopRinging } from '@/utils/ringSound';

interface ReceptionistCalledModalProps {
  isOpen: boolean;
  onConfirm: () => void;
}

export const ReceptionistCalledModal = ({
  isOpen,
  onConfirm,
}: ReceptionistCalledModalProps) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (isOpen) {
      startRinging();
    } else {
      stopRinging();
    }
    return () => stopRinging();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    stopRinging();
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 animate-in zoom-in-95 duration-200">
        <div className="flex flex-col items-center text-center">
          {/* Pulsing icon */}
          <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <BellRing className="w-10 h-10 text-secondary-600" />
          </div>

          <h2 className="text-2xl font-bold text-neutral-800 mb-2">
            {t('notifications.receptionistCalledModal.doctorNeedsAssistance')}
          </h2>
          <p className="text-neutral-500 text-sm mb-6">
            {t(
              'notifications.receptionistCalledModal.aDoctorIsRequestingReceptionist',
            )}
          </p>

          <div className="w-full bg-secondary-50 border border-secondary-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <UserCheck className="w-6 h-6 text-secondary-600 flex-shrink-0" />
            <p className="text-sm font-semibold text-secondary-800 text-left">
              {t('notifications.receptionistCalledModal.pleaseHeadToTheClinic')}
            </p>
          </div>

          <button
            type="button"
            onClick={handleConfirm}
            className="w-full py-3 rounded-lg text-lg font-semibold bg-secondary hover:bg-secondary-600 text-white transition-colors"
          >
            {t('notifications.acknowledge')}
          </button>
        </div>
      </div>
    </div>
  );
};
